import { Clock, MapPin, Phone, Globe, Mail, Check, X } from "lucide-react";
import { FacebookIcon, InstagramIcon, TikTokIcon, YouTubeIcon } from "@/components/icons/SocialIcons";
import type { Listing } from "@/lib/types";

export default function SpaProfileDetails({ listing }: { listing: Listing }) {
  // Paid tiers (pro/premium) get a followed, verified outbound link marked
  // rel="sponsored" — the Google-compliant signal for monetized links. Free
  // tiers remain nofollow.
  const isPaidLink = listing.tier === "pro" || listing.tier === "premium";

  return (
    <div className="space-y-6">
      {/* Contact */}
      <div className="p-5 rounded-2xl border border-border bg-card">
        <h3 className="font-semibold text-foreground mb-4">Contact Info</h3>
        <ul className="space-y-3">
          {(listing.address || listing.city) && (
            <li className="flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <span className="text-muted-foreground">
                {listing.address && <>{listing.address}<br /></>}
                {[listing.city, listing.state].filter(Boolean).join(", ")}
                {listing.zip && ` ${listing.zip}`}
              </span>
            </li>
          )}
          {listing.phone && (
            <li className="flex items-center gap-3 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
              <a href={`tel:${listing.phone}`} className="text-muted-foreground hover:text-foreground transition-colors">
                {listing.phone}
              </a>
            </li>
          )}
          {listing.email && (
            <li className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
              <a href={`mailto:${listing.email}`} className="text-muted-foreground hover:text-foreground transition-colors">
                {listing.email}
              </a>
            </li>
          )}
          {listing.website && (
            <li className="flex items-center gap-3 text-sm">
              <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex items-center gap-2 min-w-0">
                <a
                  href={listing.website.startsWith("http") ? listing.website : `https://${listing.website}`}
                  target="_blank"
                  rel={isPaidLink ? "noopener sponsored" : "noopener noreferrer nofollow"}
                  className="text-primary hover:opacity-80 transition-opacity truncate"
                >
                  {listing.website.replace(/^https?:\/\//, "")}
                </a>
                {isPaidLink ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-semibold whitespace-nowrap">
                    <Check className="w-3 h-3" />
                    Verified Link
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-semibold whitespace-nowrap">
                    <X className="w-3 h-3" />
                    No-Follow
                  </span>
                )}
              </div>
            </li>
          )}
          {listing.social_links?.facebook && (
            <li className="flex items-center gap-3 text-sm">
              <FacebookIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <a
                href={listing.social_links.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:opacity-80 transition-opacity truncate"
              >
                Facebook
              </a>
            </li>
          )}
          {listing.social_links?.instagram && (
            <li className="flex items-center gap-3 text-sm">
              <InstagramIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <a
                href={listing.social_links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:opacity-80 transition-opacity truncate"
              >
                Instagram
              </a>
            </li>
          )}
          {listing.social_links?.tiktok && (
            <li className="flex items-center gap-3 text-sm">
              <TikTokIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <a
                href={listing.social_links.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:opacity-80 transition-opacity truncate"
              >
                TikTok
              </a>
            </li>
          )}
          {listing.social_links?.youtube && (
            <li className="flex items-center gap-3 text-sm">
              <YouTubeIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <a
                href={listing.social_links.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:opacity-80 transition-opacity truncate"
              >
                YouTube
              </a>
            </li>
          )}
        </ul>
      </div>

      {/* Hours */}
      {listing.hours && listing.hours.length > 0 && (
        <div className="p-5 rounded-2xl border border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="flex items-center gap-2 font-semibold text-foreground">
              <Clock className="w-4 h-4 text-primary" />
              Business Hours
            </h3>
            {listing.timezone && (
              <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-md font-medium">
                {listing.timezone}
              </span>
            )}
          </div>
          <ul className="space-y-2.5">
            {listing.hours.map((h) => {
              const to12h = (t: string) => {
                const [hh, mm] = t.split(":");
                if (!hh || !mm) return t;
                const hour = parseInt(hh, 10);
                const suffix = hour >= 12 ? "PM" : "AM";
                const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                return `${h12}:${mm} ${suffix}`;
              };
              const timeDisplay = h.open === "Closed" || !h.open ? "Closed" : h.open === "By Appointment" ? "By Appointment" : `${to12h(h.open)} - ${to12h(h.close)}`;
              return (
                <li key={h.day} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{h.day}</span>
                  <span className={`font-medium ${timeDisplay === "Closed" ? "text-muted-foreground/60" : "text-foreground"}`}>
                    {timeDisplay}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
